import React, {useEffect, useRef, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import InfoCircle from '@mui/icons-material/InfoRounded';
import {useData} from '../../../../context/ConfigDataContext';
import {ChargerTypes, evChargerTemplate} from '../../../../constants/evChargerConstants';
import {chargerRegistry, getChargerEntry} from '../../../../constants/chargerRegistry';
import {NodeTypes} from '../../../../constants/nodeTypes';
import {AdvancedFields, CredentialsFields, EaseeIntegration, SocketSelector} from './chargerFields';
import {Dropdown} from '../../../../../../shared/ui/Dropdown';

const EvChargerForm = (props) => {
  const {
    evChargerFormData, setEvChargerFormData, saveEvCharger,
    advancedMode,
    findNodeByName, selectedNode, treeData,
  } = useData();

  const [evChargerType, setEvChargerType] = useState('');
  const skipNextTemplateApply = useRef(false);

  // ─── Change handlers ───────────────────────────────────────────────────────

  // Number inputs return string values; coerce them to numbers (empty stays '').
  const parseValue = (e) =>
    e.target.type === 'number' && e.target.value !== '' ? Number(e.target.value) : e.target.value;

  const onChange = (e) => {
    setEvChargerFormData({...evChargerFormData, [e.target.name]: parseValue(e)});
  };

  const onCredentialsChange = (e) => {
    setEvChargerFormData({
      ...evChargerFormData,
      credentials: {...evChargerFormData?.credentials, [e.target.name]: parseValue(e)},
    });
  };

  const onIncreaseChange = (e) => {
    setEvChargerFormData({
      ...evChargerFormData,
      increase: {...evChargerFormData?.increase, [e.target.name]: parseValue(e)},
    });
  };

  const onDecreaseChange = (e) => {
    setEvChargerFormData({
      ...evChargerFormData,
      decrease: {...evChargerFormData?.decrease, [e.target.name]: parseValue(e)},
    });
  };

  // ─── Initialise form from existing node (only on modal open) ────────────────

  const initFromNode = () => {
    const node = findNodeByName(treeData, selectedNode);
    if (!node) return;
    if (node.type === NodeTypes.ADDNODE) {
      // New node — reset to empty; user picks a type via the dropdown
      setEvChargerType('');
      setEvChargerFormData({});
      return;
    }
    // Existing node — populate type + form data from the saved attributes
    const attributeKeys = Object.keys(node.attributes);
    skipNextTemplateApply.current = true;
    setEvChargerType(attributeKeys[0]);
    setEvChargerFormData(node.attributes[attributeKeys[0]] || {});
  };

  // When the modal opens, load the existing node data (if editing)
  useEffect(() => {
    initFromNode();
  }, [props.show]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the user changes charger type, apply the fresh template
  useEffect(() => {
    if (!evChargerType) return;
    // Skip when initFromNode already set the correct data from the existing node
    if (skipNextTemplateApply.current) {
      skipNextTemplateApply.current = false;
      return;
    }
    const tmpl = evChargerTemplate[evChargerType];
    if (!tmpl) {
      console.warn(`Missing evChargerTemplate for type: ${evChargerType}`);
    } else {
      setEvChargerFormData({...tmpl});
    }
  }, [evChargerType]); // eslint-disable-line react-hooks/exhaustive-deps


  // ─── Resolve entry + infer flags from template ─────────────────────────────

  const entry = getChargerEntry(evChargerType);
  const template = evChargerTemplate[evChargerType];
  const hasCredentials = !!template?.credentials;
  const isEasee = evChargerType === ChargerTypes.EASEE;
  const usesMinPower = template ? 'minPowerW' in template : false;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Modal.Header>
        <div className="d-flex align-items-center gap-2">
          <h4 className="mb-0">EV Charger</h4>
          <a href="https://lehmannch.atlassian.net/wiki/spaces/SCCKH/pages/771424259/Web+App+SCC-Cloud#EvCharger"
             target="_blank" rel="noopener noreferrer"><InfoCircle size={20}/></a>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* ── Type selector — registry-driven ───────────────────────────── */}
          <Form.Group>
            <Form.Label>Type of Charging Station</Form.Label>
            <Dropdown
              options={chargerRegistry.map((c) => ({value: c.type, label: c.label}))}
              value={evChargerType}
              onChange={(val) => setEvChargerType(val)}
              placeholder="Select"
              block
            />
          </Form.Group>

          {evChargerFormData && entry && (
            <>
              {/* ── Name (common to all) ──────────────────────────────────── */}
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control type="text" name="name" value={evChargerFormData?.name || ''} onChange={onChange}/>
              </Form.Group>

              {/* ── Credentials — inferred from template ──────────────────── */}
              {hasCredentials && (
                <CredentialsFields formData={evChargerFormData} onChange={onCredentialsChange}/>
              )}

              {/* ── Easee integration — inferred from type ────────────────── */}
              {isEasee && (
                <EaseeIntegration
                  formData={evChargerFormData}
                  setFormData={setEvChargerFormData}
                  onChange={onChange}
                />
              )}

              {/* ── Generic fields — resolved from registry string keys ───── */}
              {entry.resolvedFields.map((f) => (
                <Form.Group key={f.name}>
                  <Form.Label>{f.label}</Form.Label>
                  <Form.Control
                    type={f.type}
                    name={f.name}
                    value={evChargerFormData?.[f.name] ?? ''}
                    onChange={onChange}
                  />
                </Form.Group>
              ))}

              {/* ── Socket selector — only when socket config exists ──────── */}
              {entry.resolvedSocket && (
                <SocketSelector
                  socket={entry.resolvedSocket}
                  formData={evChargerFormData}
                  setFormData={setEvChargerFormData}
                />
              )}

              {/* ── Increase in basic mode — for socket chargers ──────────── */}
              {entry.resolvedSocket && (
                <Form.Group>
                  <Form.Label>Increase type</Form.Label>
                  <Dropdown
                    name="type"
                    options={[
                      {value: 'absolut', label: 'Absolute'},
                      {value: 'ratio', label: 'Ratio'},
                    ]}
                    value={evChargerFormData?.increase?.type || 'absolut'}
                    onChange={onIncreaseChange}
                    block
                  />
                  <Form.Label>Increase value</Form.Label>
                  <Form.Control type="number" name="value" value={evChargerFormData?.increase?.value ?? ''} onChange={onIncreaseChange}/>
                </Form.Group>
              )}

              {/* ── Advanced mode fields — minPower inferred from template ── */}
              {advancedMode && (
                <AdvancedFields
                  usesMinPower={usesMinPower}
                  formData={evChargerFormData}
                  onChange={onChange}
                  onIncreaseChange={onIncreaseChange}
                  onDecreaseChange={onDecreaseChange}
                />
              )}
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => saveEvCharger(evChargerType)} disabled={!evChargerType}>
          Save
        </Button>
      </Modal.Footer>
    </>
  );
};

export default EvChargerForm;

